
import classNames from "classnames";
import { getPositionOnViewport } from "helpers/position";
import isEmpty from "lodash/isEmpty";
import PropTypes from "prop-types";
import * as React from "react";
import Portal from "react-portal-minimal";
import styles from "./Tooltip.scss";

const CURSOR_WIDTH = 18;
const CURSOR_HEIGHT = 27;
const ELEMENT_OFFSET_X = 4;
const ELEMENT_OFFSET_Y = 2;
const TOOLTIP_SHOW_DELAY = 50;

class TooltipManager {

  tooltipTarget: any

  constructor() {
    this.tooltips = Object.create(null);
    this.activeTooltip = null;

    this.onMouseOver = this.onMouseOver.bind(this);
    this.onMouseMove = this.onMouseMove.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
    this.onEscape = this.onEscape.bind(this);
    this.destroyActiveTooltip = this.destroyActiveTooltip.bind(this);
  }

  addTooltip(id, tooltip) {
    if (this.tooltips[id]) throw `duplicate tooltip id - ${id}`;
    if (isEmpty(this.tooltips)) document.addEventListener('mouseover', this.onMouseOver);
    this.tooltips[id] = tooltip;
  }

  removeTooltip(id) {
    const tooltip = this.tooltips[id];
    if (!tooltip) {
      /* eslint-disable no-console */
      console.warn(`try delete tooltip id "${id}" that does not exist`);
      return;
    }
    if (tooltip === this.activeTooltip) this.destroyActiveTooltip();
    delete this.tooltips[id];
    if (isEmpty(this.tooltips)) document.removeEventListener('mouseover', this.onMouseOver);
  }

  onMouseLeave(e) {
    const relatedTarget = e.relatedTarget && this.getClosestParentWithTooltip(e.relatedTarget);
    if (relatedTarget && e.target.dataset.for === relatedTarget.dataset.for && e.target.dataset.tip === relatedTarget.dataset.tip) {
      this.removeListeners();
      this.addListeners(relatedTarget);
    } else {
      this.destroyActiveTooltip();
    }
  }

  onMouseOver(e) {
    const target = this.getClosestParentWithTooltip(e.target);
    if (target && target.dataset.for) {
      const tooltip = this.tooltips[target.dataset.for];
      if (tooltip && this.activeTooltip !== tooltip) {
        this.activateTooltip(tooltip, target);
      }
    }
  }

  onMouseMove(e) {
    this.activeTooltip.move(e);
  }

  onEscape(e) {
    if (e.keyCode === 27 && this.activeTooltip) {
      e.preventDefault();
      this.destroyActiveTooltip();
    }
  }

  activateTooltip(tooltip, target) {
    if (this.activeTooltip) this.destroyActiveTooltip();
    this.activeTooltip = tooltip;
    this.activeTooltip.activate(target.dataset.tip);
    this.addListeners(target);
    document.addEventListener('keydown', this.onEscape, true);
    document.getElementById('app').addEventListener('scroll', this.destroyActiveTooltip);
  }

  destroyActiveTooltip() {
    if (!this.activeTooltip) return;
    this.activeTooltip.hide();
    this.activeTooltip = null;
    document.removeEventListener('keydown', this.onEscape, true);
    document.getElementById('app').removeEventListener('scroll', this.destroyActiveTooltip);
    this.removeListeners();
  }

  addListeners(target) {
    this.tooltipTarget = target;
    this.tooltipTarget.addEventListener('contextmenu', this.destroyActiveTooltip);
    this.tooltipTarget.addEventListener('mousemove', this.onMouseMove, true);
    this.tooltipTarget.addEventListener('mouseleave', this.onMouseLeave);
  }

  removeListeners() {
    this.tooltipTarget.removeEventListener('contextmenu', this.destroyActiveTooltip);
    this.tooltipTarget.removeEventListener('mousemove', this.onMouseMove, true);
    this.tooltipTarget.removeEventListener('mouseleave', this.onMouseLeave);
    this.tooltipTarget = null;
  }

  updateActiveTooltip() {
    if (!this.activeTooltip) return;
    const target = this.tooltipTarget;
    const tooltip = this.tooltips[target.dataset.for];
    if (tooltip !== this.activeTooltip) this.activateTooltip(tooltip, target);
  }

  getClosestParentWithTooltip(target) {
    while (!target.dataset.for) {
      target = target.parentNode;
      if (target === document) return;
    }
    return target;
  }
}

class Tooltip extends React.PureComponent {

  static propTypes = {
    children: PropTypes.node,
    id: PropTypes.string.isRequired,
    width: PropTypes.string,
    isDemo: PropTypes.bool,

    getContent: PropTypes.func
  };

  static defaultProps = {
    getContent: () => { }
  };

  static manager = new TooltipManager();

  constructor(props) {
    super(props);

    this.state = {
      isVisible: false,
      top: 0,
      left: 0
    };

    this.show = this.show.bind(this);
    this.hide = this.hide.bind(this);
  }

  componentDidMount() {
    Tooltip.manager.addTooltip(this.props.id, this);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {// eslint-disable-line
    if (nextProps.id !== this.props.id) {
      Tooltip.manager.addTooltip(nextProps.id, this);
      Tooltip.manager.removeTooltip(this.props.id);
    }
  }

  componentWillUnmount() {
    Tooltip.manager.removeTooltip(this.props.id);
  }

  activate(tooltipTip) {
    this.tooltipTip = tooltipTip;
    this.timeoutId = setTimeout(this.show, TOOLTIP_SHOW_DELAY);
  }

  move(e) {
    this.setPosition(e);
  }

  show() {
    this.setState({ isVisible: true });
  }

  hide() {
    this.clearTimeouts();
    this.setState({ isVisible: false });
    this.tooltipTip = null;
  }

  setPosition(e) {
    const initialPosition = {
      top: e.clientY,
      left: e.clientX
    };
    const position = getPositionOnViewport(this._tooltip, initialPosition, {
      CURSOR_WIDTH,
      CURSOR_HEIGHT,
      ELEMENT_OFFSET_X,
      ELEMENT_OFFSET_Y
    });
    this.setState(position);
  }

  clearTimeouts() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
  }

  render() {
    const classNameTooltip = classNames(styles.tooltip, {
      [styles.isVisible]: this.state.isVisible,
      [styles.isDemo]: this.props.isDemo
    });

    const style = {
      top: this.state.top,
      left: this.state.left,
      maxWidth: this.props.width ? this.props.width : null
    };

    const content = this.props.children || this.tooltipTip && this.props.getContent(this.tooltipTip);

    const tooltip = <div className={classNameTooltip} style={style} ref={c => this._tooltip = c}>
      {content}
    </div>;

    return this.props.isDemo ? tooltip : <Portal>{tooltip}</Portal>;
  }
}

export default Tooltip;